<template>
  <div>
    <v-dialog v-model="dialog" max-width="600" scrollable>
      <template v-slot:activator="{ on, attrs }">
        <v-btn color="primary" v-bind="attrs" v-on="on" text small>
          <v-icon small color="primary" class="mr-2"
            >mdi-compare-horizontal</v-icon
          >
          Compare this to another Spherical GP
        </v-btn>
        <div class="caption ml-4">
          (For example, the patient's current GP lens)
        </div>
      </template>

      <v-card>
        <div class="orangeCardTitle d-flex flex-row pa-3">
          <div>
            <div class="title">Compare Spherical GPs</div>
            <div class="caption">
              For example, how does a calculated lens compare to the patient's
              current lens - after compensating for different base curves and
              powers?
            </div>
          </div>

          <v-spacer></v-spacer>

          <v-btn icon text @click="dialog = false">
            <v-icon color="primary">mdi-close</v-icon></v-btn
          >
        </div>
        <v-card-text class="darkerCardText pt-3">
          <div class="">
            <div v-if="keratometry" class="my-4">
              <b>Keratometry</b><br />
              {{ prettyFullKstringDandMM(keratometry) }}
            </div>

            <v-scale-transition>
              <table class="width100p" v-if="editing">
                <tr>
                  <th v-if="$vuetify.breakpoint.smAndUp" class="uCmell"></th>
                  <th class="uCmell" :style="styleForLensNum[0]">Lens 1</th>
                  <th class="uCmell" :style="styleForLensNum[1]">Lens 2</th>
                </tr>
                <tr>
                  <th v-if="$vuetify.breakpoint.smAndUp" class="uCmell">
                    Power
                  </th>
                  <td class="uCmell" :style="styleForLensNum[0]">
                    {{ inputs.pwr0 }}
                  </td>
                  <td class="uCmell" :style="styleForLensNum[1]">
                    <div class="anInput">
                      <v-text-field
                        clearable
                        v-model="inputs.pwr1"
                        label="Lens 2 Power"
                        dense
                        outlined
                        class="mt-1 mr-2 whtBkg"
                        @keyup.enter="formatDiopterInput('pwr1')"
                        @blur="formatDiopterInput('pwr1')"
                        hide-details="auto"
                        @input="dirty = true"
                      ></v-text-field>
                    </div>
                  </td>
                </tr>
                <tr>
                  <th v-if="$vuetify.breakpoint.smAndUp" class="uCmell">BC</th>
                  <td class="uCmell" :style="styleForLensNum[0]">
                    <!-- {{ inputs.bc0 }}<br /> -->
                    {{ bcMessages.bc0 }}
                  </td>
                  <td class="uCmell" :style="styleForLensNum[1]">
                    <div class="anInput">
                      <v-text-field
                        clearable
                        v-model="inputs.bc1"
                        label="Lens 2 Base Curve"
                        dense
                        outlined
                        class="mt-1 mr-2 whtBkg"
                        @keyup.enter="formatKInput('bc1')"
                        @blur="formatKInput('bc1')"
                        hide-details="auto"
                        @input="dirty = true"
                      ></v-text-field>
                      <div class="caption">
                        {{ bcMessages.bc1 }}
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td colspan="3" class="text-right">
                    <v-btn
                      @click="compareRGPcalc()"
                      small
                      color="primary"
                      :disabled="!canCalculate && dirty"
                      depressed
                      class="mt-2"
                    >
                      Compare Lenses</v-btn
                    >
                  </td>
                </tr>
              </table>
            </v-scale-transition>
          </div>
          <!-- {{ objects }} -->

          <div
            v-if="canCalculate && !dirty && !editing"
            class="d-flex flex-row flex-wrap justify-space-around my-4"
          >
            <div v-for="lensNum in [0, 1]" :key="'lens' + lensNum">
              <v-card class="my-2" min-width="250" :color="colors[lensNum]">
                <div class="title d-flex flex-row align-center justify-center">
                  <div>Lens {{ lensNum + 1 }}</div>
                  <div v-if="lensNum == 1" class="ml-2">
                    <v-btn icon x-small @click="editing = true" color="primary">
                      <v-icon x-small>mdi-pencil</v-icon>
                    </v-btn>
                  </div>
                </div>
                <v-card-text class="darkerCardText py-2">
                  <table>
                    <tr>
                      <th class="text-left">Base Curve</th>
                      <td>
                        {{ prettyKstringDandMM(objects["bc" + lensNum].k1) }}
                      </td>
                    </tr>
                    <tr>
                      <th class="text-left">Power</th>
                      <td>
                        {{ prettyStringMinus(objects["pwr" + lensNum]) }}
                      </td>
                    </tr>
                    <tr>
                      <th class="text-left">Tear Lens</th>
                      <td>
                        {{ prettyStringMinus(tearLensObj[lensNum]) }}
                      </td>
                    </tr>
                    <tr>
                      <th class="text-left">Total Power</th>
                      <td>
                        {{ prettyStringMinus(totalPowerObj[lensNum]) }}
                      </td>
                    </tr>
                  </table>
                </v-card-text>
              </v-card>
            </div>
          </div>

          <v-expand-transition>
            <div v-if="!dirty && !editing" class="discussionClass">
              <ul v-if="comparisons">
                <li>
                  Lens 1 is
                  {{ comparisons.bcDiff }}
                  <i>{{ comparisons.whichWayBC }}</i> than Lens 2.
                </li>
                <li>
                  Lens 1 is correcting
                  {{ comparisons.powerDiff }}
                  <i>{{ comparisons.whichWayPower }}</i> than Lens 2 (when
                  taking into account total corrected power).
                </li>
              </ul>
              <!-- <div class="overline mt-3">Lens Comparisons</div>
              <div v-html="discussion"></div> -->
              <div v-if="totalPowerObj[0] && totalPowerObj[1]" class="mt-3">
                <div class="overline mt-4">Comparisons, Illustrated</div>
                <div class="d-flex flex-row flex-wrap justify-space-around">
                  <div v-for="i in [0, 1]" :key="i">
                    <div class="d-flex flex-column flex-wrap">
                      <v-card class="aBox mx-1 mb-4" :color="colors[i]">
                        <div class="overline">Lens Power {{ i + 1 }}</div>
                        <optical-cross-image
                          :obj="objects['pwr' + i]"
                          :dimensions="crossSize"
                          class="mx-auto"
                        >
                        </optical-cross-image>
                        <div class="caption crossCaption">
                          {{ prettyStringMinus(objects["pwr" + i]) }}
                          <!-- {{ prettyKstringDandMM(objects["bc" + i].k1) }} -->
                        </div>
                      </v-card>
                      <v-card class="aBox mx-1 mb-4" :color="colors[i]">
                        <div class="overline">
                          Tear Lens {{ i + 1 }}
                          <tear-lens-calc-dialog
                            class="d-inline-block"
                            :keratometry="keratometry"
                            :bc="[objects.bc0, objects.bc1]"
                            :tearlens="tearLensObj"
                            :colors="colors"
                          >
                          </tear-lens-calc-dialog>
                        </div>

                        <optical-cross-image
                          :obj="tearLensObj[i]"
                          :dimensions="crossSize"
                          type="NaFl"
                          :showHeatmap="true"
                          class="mx-auto"
                        ></optical-cross-image>
                        <div class="caption crossCaption">
                          {{ prettyStringMinus(tearLensObj[i]) }}
                        </div>
                      </v-card>
                      <v-card class="aBox mx-1 mb-4" :color="colors[i]">
                        <div class="overline">Total Power {{ i + 1 }}</div>
                        <optical-cross-image
                          :obj="totalPowerObj[i]"
                          :dimensions="crossSize"
                          class="mx-auto"
                        ></optical-cross-image>
                        <div class="caption crossCaption">
                          {{ prettyStringMinus(totalPowerObj[i]) }}
                        </div>
                      </v-card>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-expand-transition>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// import Name from "../dir/file";
import RxMethods from "@/assets/js/cls/RxMethods";
import OpticalCrossImage from "@/components/clStuff/OpticalCrossImage";
import TearLensCalcDialog from "./TearLensCalcDialog.vue";

export default {
  name: "CompareRgps",
  components: { OpticalCrossImage, TearLensCalcDialog },
  mixins: [RxMethods],
  props: {
    lens1Power: null, // will be index 0
    lens1BC: null, // will be index 0
    keratometry: null,
  },

  data: function () {
    return {
      dialog: false,
      inputs: { pwr0: "", pwr1: "", bc0: "", bc1: "" },
      objects: { pwr0: "", pwr1: "", bc0: "", bc1: "" },
      discussion: null,
      dirty: true,
      editing: true,
      crossSize: 100,
      colors: ["#fcf5f5", "#fffce2"],
    };
  },
  methods: {
    formatDiopterInput(input) {
      if (this.objects[input])
        this.inputs[input] = this.numToDiopterString(this.objects[input].sph);
    },
    formatKInput(input) {
      if (this.objects[input])
        this.inputs[input] = this.prettyKstring(this.objects[input].k1);
    },
    compareRGPcalc: function () {
      this.dirty = false;
      this.editing = false;
    },
  },
  computed: {
    styleForLensNum: function () {
      let txt = "background-color: ";
      let rtn = [];
      rtn[0] = txt + this.colors[0] + ";";
      rtn[1] = txt + this.colors[1] + ";";
      return rtn;
    },
    bcMessages: function () {
      let rtn = { bc0: null, bc1: null };
      if (this.objects.bc0?.k1)
        rtn.bc0 = this.prettyKstringDandMM(this.objects.bc0.k1, true);
      if (this.objects.bc1?.k1) {
        rtn.bc1 = this.prettyKstringDandMM(this.objects.bc1.k1, true);
      }
      return rtn;
    },
    canCalculate: function () {
      return (
        this.objects.bc0?.k1 &&
        this.objects.bc1?.k1 &&
        this.objects.pwr0?.sph &&
        this.objects.pwr1?.sph
      );
    },
    tearLensObj: function () {
      let rtn = [null, null];
      if (!this.keratometry) return null;
      [0, 1].forEach((i) => {
        let bcObj = this.objects["bc" + i];
        if (bcObj) {
          let bc = parseFloat(this.kValueD(bcObj.k1));
          rtn[i] = this.tearLensPower(this.keratometry, bc, bc);
        }
      });
      return rtn;
    },

    totalPowerObj: function () {
      let rtn = [null, null];
      [0, 1].forEach((i) => {
        let powerObj = this.objects["pwr" + i];
        let tearLensObj = this.tearLensObj[i];
        if (powerObj && tearLensObj) {
          rtn[i] = this.addRx(powerObj, tearLensObj);
        }
      });
      return rtn;
    },
    comparisons: function () {
      if (!this.canCalculate) return null;
      let rtn = {};
      let bc1 = parseFloat(this.kValueD(this.objects.bc0.k1));
      let bc2 = parseFloat(this.kValueD(this.objects.bc1.k1));

      let bcDiff = bc1 - bc2;
      rtn.bcDiff = this.numToDiopterString(Math.abs(bcDiff), 0, 0.125) + " D ";
      rtn.whichWayBC = bcDiff < 0 ? "flatter" : "steeper";

      let pwr0 = this.sphM(this.totalPowerObj[0]);
      let pwr1 = this.sphM(this.totalPowerObj[1]);

      let powerDiff = pwr0 - pwr1;
      rtn.powerDiff =
        this.numToDiopterString(Math.abs(powerDiff), 0, 0.125) + " D ";

      let whichWayPower = "more minus";
      if (pwr0 > 0 && powerDiff > 0) whichWayPower = "more plus";
      if (pwr0 > 0 && powerDiff < 0) whichWayPower = "less plus";
      if (pwr0 < 0 && powerDiff > 0) whichWayPower = "less minus";
      rtn.whichWayPower = whichWayPower;
      return rtn;
    },
  },
  watch: {
    inputs: {
      handler: function (val) {
        this.objects.pwr0 = this.parseRxString(val.pwr0);
        this.objects.pwr1 = this.parseRxString(val.pwr1);
        this.objects.bc0 = this.parseKstring(val.bc0);
        this.objects.bc1 = this.parseKstring(val.bc1);
      },
      deep: true,
    },
    lens1Power: function (val) {
      this.objects.pwr0 = this.parseRxString(val);
      this.inputs.pwr0 = this.numToDiopterString(this.objects.pwr0?.sph);
    },
    lens1BC: function (val) {
      this.objects.bc0 = this.parseKstring(val);
      this.inputs.bc0 = this.prettyKstring(val);
    },
  },
  mounted() {
    if (this.lens1Power) {
      //   console.log(this.lens1Power);
      this.objects.pwr0 = this.parseRxString(this.lens1Power);
      this.inputs.pwr0 = this.numToDiopterString(this.objects.pwr0?.sph);
    }
    if (this.lens1BC) {
      this.objects.bc0 = this.parseKstring(this.lens1BC);
      this.inputs.bc0 = this.prettyKstring(this.lens1BC);
    }
  },
};
</script>

<style scoped>
/* .discussionClass >>> .compareRGPbox {
  padding: 7px;
  margin: 0 1em 1em 0;
  background-color: #f0f8ff;
  min-width: 250px;
}
.discussionClass >>> th {
  text-align: left;
  padding-right: 1em;
} */
.anInput {
  max-width: 200px;
  margin: 0 auto 0.5em;
}
.aBox {
  /* min-height: 180px; */
  text-align: center;
}
.crossCaption {
  margin: 0.75em 0;
}
table {
  border-spacing: 0;
  border-collapse: collapse;
}
td {
  padding-left: 10px;
}
td.uCmell,
th.uCmell {
  text-align: center;
}
.uCmell {
  padding: 5px 10px;
  border: 1px solid #e6e6e6;
}
.whtBkg {
  background-color: white;
}
</style>
